/* eslint-disable max-classes-per-file */
import React, {
    ReactElement, useContext, useEffect, useState, createRef,
} from "react";
import { ListGroup, Modal, Offcanvas } from "react-bootstrap";
import { BiCommand } from "react-icons/bi";
import {
    HiArrowNarrowRight, HiArrowNarrowDown, HiArrowNarrowUp, HiArrowNarrowLeft,
} from "react-icons/hi";
import { RiSearchLine } from "@remixicon/react";
import classNames from "classnames";
import { useTheme } from "./Theme";
import FormGroup from "../components/theme/form-group/FormGroup";

const IS_OSX = window.navigator.userAgent.includes("Macintosh;");
const DEFAULT_CONTEXT = "default";
export type Handler = (event?: KeyboardEvent) => void;
export interface Shortcut {
    shortcut: string;
    context: string;
    description?: string;
    group?: string;
    shouldUseAllContexts?: boolean;
    isSystem?: boolean;
    handler: Handler
}

export class ScopedClientApi {
    private handlers: Shortcut[] = [];

    private api: ShortcutApi;

    constructor(api: ShortcutApi) {
        this.api = api;
    }

    public on(shortcut: Shortcut): void {
        this.handlers.push(shortcut);
        this.api.on(shortcut);
    }

    public addReset(key: string): void {
        this.api.addResetState(key);
    }

    public allOff(): void {
        this.api.off(...this.handlers);
    }
}

const KEY_MAP: Record<string, string> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
    Escape: "esc",
};

const ANY_CONTEXT = new Set(["Escape"]);
const IGNORE_CONTEXT = new Set(["INPUT", "TEXTAREA"]);

const KEY_TIMEOUT = 1000;

export interface KeyboardContextState {

    pushContext(value: string): void
    setContext(value: string): void
    popContext(value: string): void
    toggleShortcutsMenu(): void
    hasCustomCommands(context: string): boolean;
}

export class ShortcutApi implements KeyboardContextState {
    public static instance = new ShortcutApi();

    private events: Record<string, Shortcut[]> = Object.create(null);

    private resetStateSet = new Set<string>();

    private _context: string[] = [DEFAULT_CONTEXT];

    private _onToggleShortcuts?: (() => void);

    constructor() {
        let keyCache: string[] = [];
        let timer: ReturnType<typeof setTimeout>;
        window.addEventListener("keydown", event => {
            const isIgnoreContext = IGNORE_CONTEXT.has((event.target as Element)?.tagName) && !ANY_CONTEXT.has(event.key);

            if (event.key === "Shift" || event.key === "Control" || event.key === "Alt" || event.key === "Meta") {
                return;
            }
            const keys = [];
            if (event.ctrlKey) {
                keys.push(IS_OSX ? "ctrl" : "cmd");
            }
            if (event.shiftKey) {
                keys.push("shift");
            }
            if (event.altKey) {
                keys.push("alt");
            }
            if (event.metaKey) {
                keys.push("cmd");
            }
            if (event.code === "Space") {
                keys.push("space");
            } else {
                keys.push(KEY_MAP[event.key] ?? event.key.toLowerCase());
            }

            const pressed = keyCache.concat(keys ?? []).join("+");

            let handlers = this.events[pressed]?.filter(row => row.context === this._context.lastElement);
            if (isIgnoreContext) {
                handlers = handlers?.filter(row => row.shouldUseAllContexts);
                if (handlers?.isEmpty) {
                    return;
                }
            }

            if (handlers?.isNotEmpty) {
                handlers.forEach(shortcut => shortcut.handler(event));
                event.preventDefault();
                keyCache = [];
            } else if (this.resetStateSet.has(pressed) || isIgnoreContext) {
                keyCache = [];
            } else {
                keyCache.push(keys.join("+"));

                if (timer) {
                    clearTimeout(timer);
                }
                timer = setTimeout(() => {
                    keyCache = [];
                }, KEY_TIMEOUT);
            }
        });
    }

    public hasCommands() {
        return Object.keys(this.events).isNotEmpty;
    }

    public hasCustomCommands(context?: string) {
        return Object.values(this.events)
            .some(row => row.some(event => !event.isSystem && (!context || event.context === context)));
    }

    public addResetState(key: string): void {
        this.resetStateSet.add(key);
    }

    public pushContext(value: string) {
        if (this._context.lastElement !== value) {
            this._context.push(value);
        }
    }

    public onToggleShortcuts(handler: (() => void)) {
        this._onToggleShortcuts = handler;
    }

    public toggleShortcutsMenu(): void {
        this._onToggleShortcuts?.();
    }

    public setContext(value: string) {
        this._context = [value];
    }

    public popContext(value: string) {
        const index = this._context.lastIndexOf(value);
        if (index >= 0) {
            this._context.splice(index, 1);
        }
        // if ((value && this._context.lastElement === value) || !value) {
        //     this._context.pop();
        // }
    }

    public registerEvents(handler: (api: ScopedClientApi) => void): void {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const client = new ScopedClientApi(this);
            handler(client);
            return () => {
                client.allOff();
            };
        // eslint-disable-next-line react-hooks/exhaustive-deps
        });
    }

    public shortcutGroups(search: string): ({group: string, items: Shortcut[]})[] {
        let rows = Object.values(this.events).flatten().filter(row => row.description && row.group) as Required<Shortcut>[];
        if (search) {
            const searchStr = search.toLowerCase();
            rows = rows.filter(row => row.description.toLowerCase().includes(searchStr)
                                        || row.shortcut.includes(searchStr)
                                        || row.shortcut.replace("cmd", "ctrl").includes(searchStr)
                                        || row.group.toLowerCase().includes(searchStr));
        }

        const groups = rows.groupBy(row => row.group);
        return Object.entries(groups).map(([group, items]) => ({
            group,
            items,
        })).sortBy("group");
    }

    public search(search: string, context: string): Shortcut[] {
        const rows = Object.values(this.events).flatten().filter(row => row.description && row.group) as Required<Shortcut>[];
        const searchStr = search.toLowerCase();
        return rows.filter(row => row.context === context
                                && row.shortcut !== "esc"
                                && (row.description.toLowerCase().includes(searchStr)
                                    || row.shortcut.includes(searchStr)
                                    || row.shortcut.replace("cmd", "ctrl").includes(searchStr)));
    }

    public on(shortcut: Shortcut): void {
        this.events[shortcut.shortcut] = this.events[shortcut.shortcut] ?? [];
        this.events[shortcut.shortcut].push(shortcut);
    }

    public off(...shortcuts: Shortcut[]): void {
        shortcuts.forEach(shortcut => {
            const list = this.events[shortcut.shortcut];
            if (!list) {
                return;
            }

            const index = list.findIndex(item => item === shortcut);
            if (index >= 0) {
                list.splice(index, 1);
            }
        });
    }
}

export const KeyboardShortcutContext = React.createContext<ShortcutApi>(ShortcutApi.instance);

const ICON_MAP: Record<string, ReactElement> = {
    cmd: <BiCommand />,
    up: <HiArrowNarrowUp />,
    down: <HiArrowNarrowDown />,
    left: <HiArrowNarrowLeft />,
    right: <HiArrowNarrowRight />,
};
function ShortcutIcons({
    shortcut, className, isThemeV3,
}: {
    shortcut: string; className: string, isThemeV3: boolean
}): ReactElement {
    const parts = shortcut.split("+");

    return (
        <span className={className}>
            {parts.map(item => {
                const btn = item === "cmd" && !IS_OSX ? "ctrl" : item;
                return (
                    <span
                        className={classNames({
                            "badge me-1": true,
                            "bg-gray-600": !isThemeV3,
                            "badge-primary": isThemeV3,
                        })}
                        key={item}
                    >
                        {ICON_MAP[btn] ?? btn}

                    </span>
                );
            })}
        </span>
    );
}

const KEYBOARD_CONTEXT = "keyboard-actions";

export function KeyboardShortcutProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [isShow, setShow] = useState(false);
    const [isShowModal, setShowModal] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const theme = useTheme();

    const [search, setSearch] = useState("");
    const searchRef = createRef<HTMLInputElement>();

    const onHide = () => {
        setShowModal(false);
        setSearch("");
        setSelectedIndex(0);
        ShortcutApi.instance.popContext(KEYBOARD_CONTEXT);
    };

    const searchResults = ShortcutApi.instance.search(search, DEFAULT_CONTEXT).dedupe(row => row.shortcut).limit(10);

    const selected = (selectedIndex >= 0
        ? (selectedIndex % searchResults.length)
        : Math.abs(searchResults.length + ((selectedIndex + 1) % searchResults.length)) - 1);

    ShortcutApi.instance.registerEvents(api => {
        api.on({
            shortcut: "shift+?",
            isSystem: true,
            context: DEFAULT_CONTEXT,
            group: "General",
            description: "Shows this help screen.",
            handler: () => {
                setShow(true);
            },
        });
        api.on({
            shortcut: "esc",
            isSystem: true,
            context: DEFAULT_CONTEXT,
            group: "General",
            description: "Hides the current help screen.",
            handler: () => {
                setShow(false);
            },
        });
        api.on({
            shortcut: "cmd+k",
            isSystem: true,
            context: DEFAULT_CONTEXT,
            group: "General",
            description: "Foqal Command menu.",
            handler: () => {
                ShortcutApi.instance.pushContext(KEYBOARD_CONTEXT);
                setShowModal(true);
            },
        });
        api.on({
            shortcut: "esc",
            isSystem: true,
            context: KEYBOARD_CONTEXT,
            handler: onHide,
        });
        api.on({
            shortcut: "down",
            isSystem: true,
            context: KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: evt => {
                evt?.preventDefault();
                setSelectedIndex(curr => curr + 1);
            },
        });
        api.on({
            shortcut: "up",
            isSystem: true,
            context: KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: evt => {
                evt?.preventDefault();
                setSelectedIndex(curr => curr - 1);
            },
        });
        api.on({
            shortcut: "enter",
            isSystem: true,
            context: KEYBOARD_CONTEXT,
            shouldUseAllContexts: true,
            handler: () => {
                const command = searchResults[selected];
                command?.handler();
                onHide();
            },
        });
    });
    ShortcutApi.instance.onToggleShortcuts(() => setShow(!isShow));
    useEffect(() => {
        if (isShow || isShowModal) {
            searchRef.current?.focus();
        }
    }, [isShow, isShowModal, searchRef]);

    const groups = ShortcutApi.instance.shortcutGroups(search);
    const isEmpty = groups.count(row => row.group !== "General") === 0;
    if (isEmpty && !search) {
        return children;
    }
    return (
        <KeyboardShortcutContext.Provider value={ShortcutApi.instance}>
            <Modal
                show={isShowModal}
                onHide={onHide}
                contentClassName="rounded shadow border-0"

            >
                <Modal.Body className="overflow-auto flex-grow-1 flex-shrink-1 bg-blue-dark text-white rounded">
                    <div>
                        <small className="text-blue-light fw-lighter">
                            Foqal Command
                        </small>
                        <input
                            ref={searchRef}
                            type="search"
                            placeholder="Search..."
                            className="w-100 rounded no-border text-white mt-1"
                            value={search}
                            onChange={evt => setSearch(evt.target.value)}
                        />

                        {search && searchResults.isNotEmpty && (
                            <ListGroup variant="flush" className="mt-4">
                                {searchResults?.map((item, index) => {
                                    const classes = ["border-bottom-0 my-1 d-flex justify-content-between align-items-center "];

                                    if (index === selected) {
                                        classes.push("bg-light text-black");
                                    } else {
                                        classes.push("bg-blue-dark text-light");
                                    }
                                    return (
                                        <ListGroup.Item
                                            className={classes.join(" ")}
                                            key={`search-${item.context}${item.shortcut}`}
                                        >
                                            <small className="col-8">
                                                {item.description}
                                            </small>
                                            <ShortcutIcons shortcut={item.shortcut} className="col" isThemeV3={theme.isV3} />
                                        </ListGroup.Item>

                                    );
                                })}
                            </ListGroup>
                        )}
                    </div>
                </Modal.Body>

            </Modal>
            <Offcanvas title="Shortcuts" show={isShow} onHide={() => setShow(false)} placement="end">
                <Offcanvas.Header>
                    {theme.isV3 ? (
                        <FormGroup
                            size="filter"
                            isRequired
                            ref={searchRef}
                            type="search"
                            prefixIcon={<RiSearchLine />}
                            placeholder="Search..."
                            aria-label="Search"
                            value={search}
                            groupClassName="m3b-1 w-100"
                            onChange={event => setSearch(event.target.value)}
                        />

                    ) : (
                        <input
                            ref={searchRef}
                            type="search"
                            placeholder="Search..."
                            className="w-100 no-border"
                            value={search}
                            onChange={evt => setSearch(evt.target.value)}
                        />
                    )}

                </Offcanvas.Header>
                <Offcanvas.Body>
                    {groups.isEmpty && (
                        <div className="text-center">
                            <small>
                                No shortcuts found. Please try searching for something else.
                            </small>
                        </div>
                    )}
                    {groups.map(({ group, items }) => (
                        <div className="mb-3" key={group}>
                            <h6>{group}</h6>
                            <ListGroup variant="flush">
                                {items.map(item => (
                                    <ListGroup.Item
                                        className="border-bottom-0 pb-0 d-flex justify-content-between card-transparent"
                                        key={item.shortcut}
                                    >
                                        <small className="col-8">
                                            {item.description}
                                        </small>
                                        <ShortcutIcons shortcut={item.shortcut} className="col" isThemeV3={theme.isV3} />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    ))}
                </Offcanvas.Body>
            </Offcanvas>
            {children}
        </KeyboardShortcutContext.Provider>
    );
}

export default function useKeyboardShortcuts(handler: (api: ScopedClientApi) => void): void {
    const api = useContext(KeyboardShortcutContext) ?? {};
    api.registerEvents(handler);
}

export function useKeyboardState(): KeyboardContextState {
    return useContext(KeyboardShortcutContext);
}
